import { useState, useMemo } from "react";
import { useListTasks, useListInstances, Task, RecurrencePattern } from "@/lib/api/checklist";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getDateRangeForRecurrence } from "@/lib/utils/recurrence";
import { BiannuallyBreakdownView } from "./BiannuallyBreakdownView";
import { Skeleton } from "@/components/ui/skeleton";

interface BiannualBreakdownCardProps {
  searchQuery?: string;
}

export function BiannuallyBreakdownCard({ searchQuery = '' }: BiannualBreakdownCardProps) {
  const [lookbackHalfYears, setLookbackHalfYears] = useState(4);
  const [isFiscalYear, setIsFiscalYear] = useState(false);

  // Fetch all tasks
  const tasksResult = useListTasks({
    is_active: true,
    limit: 100000,
    search: searchQuery || undefined,
  });

  // Calculate date range for biannually
  const dateRange = useMemo(() => {
    return getDateRangeForRecurrence(RecurrencePattern.BIANNUALLY, lookbackHalfYears);
  }, [lookbackHalfYears]);

  // Fetch all instances within the date range
  const instancesResult = useListInstances({
    due_date_from: dateRange.start.toISOString(),
    due_date_to: dateRange.end.toISOString(),
    limit: 100000,
    search: searchQuery || undefined,
  });

  const tasks = tasksResult?.data?.data?.tasks || [];
  const instances = instancesResult?.data?.data?.instances || [];
  const isLoading = tasksResult.isLoading || instancesResult.isLoading;

  // Filter tasks by biannually recurrence pattern
  const filteredTasks = useMemo(() => {
    return tasks.filter((task: Task) => task.recurrence_pattern === RecurrencePattern.BIANNUALLY);
  }, [tasks]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Biannual Tasks</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Label htmlFor="lookback-half-years" className="whitespace-nowrap">
              Show last:
            </Label>
            <Input
              id="lookback-half-years"
              type="number"
              min="2"
              max="12"
              value={lookbackHalfYears}
              onChange={(e) => setLookbackHalfYears(parseInt(e.target.value) || 1)}
              className="w-20"
            />
            <span className="text-sm text-muted-foreground">half-years</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Switch
              id="fiscal-year-biannually"
              checked={isFiscalYear}
              onCheckedChange={setIsFiscalYear}
            />
            <Label htmlFor="fiscal-year-biannually" className="text-sm">
              Fiscal Year View
            </Label>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No tasks found with biannual recurrence
          </div>
        ) : (
          <BiannuallyBreakdownView
            tasks={filteredTasks}
            instances={instances}
            lookbackHalfYears={lookbackHalfYears - 1}
            isFiscalYear={isFiscalYear}
          />
        )}
      </CardContent>
    </Card>
  );
}
